import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge, money, jobTime } from "@/components/status";
import { Briefcase, Home, Clock, DollarSign, ArrowRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="border border-border rounded-md bg-white p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
        <Icon className="w-4 h-4 text-ocean" />
      </div>
      <div className="font-heading font-black text-3xl text-sapphire mt-2">{value}</div>
    </div>
  );
}

export default function PMDashboard() {
  const [jobs, setJobs] = useState([]);
  const [properties, setProperties] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data));
    api.get("/properties").then((r) => setProperties(r.data));
  }, []);

  const active = jobs.filter((j) => ["open", "pending", "accepted", "in_progress", "completed"].includes(j.status));
  const paid = jobs.filter((j) => j.status === "paid");
  const spend = paid.reduce((s, j) => s + j.payout_amount, 0);
  const recent = [...jobs].sort((a, b) => (b.created_at > a.created_at ? 1 : -1)).slice(0, 6);

  return (
    <div className="space-y-8" data-testid="pm-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight text-sapphire">Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-1">Your turnover operations at a glance.</p>
        </div>
        <Button className="bg-ocean hover:bg-ocean/90" onClick={() => navigate("/pm/jobs")} data-testid="dashboard-new-job">
          <Plus className="w-4 h-4 mr-1" /> New Job
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Home} label="Properties" value={properties.length} testid="stat-properties" />
        <Stat icon={Briefcase} label="Active Jobs" value={active.length} testid="stat-active" />
        <Stat icon={Clock} label="Completed" value={paid.length} testid="stat-completed" />
        <Stat icon={DollarSign} label="Total Paid" value={money(spend)} testid="stat-spend" />
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-bold text-xl text-sapphire">Recent Jobs</h2>
          <button className="text-sm text-ocean flex items-center gap-1" onClick={() => navigate("/pm/jobs")} data-testid="view-all-jobs">
            View pipeline <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        <div className="border border-border rounded-md bg-white divide-y divide-border">
          {recent.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">No jobs yet. Create your first job.</div>}
          {recent.map((j) => (
            <button
              key={j.id}
              onClick={() => navigate(`/jobs/${j.id}`)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors text-left"
              data-testid={`job-row-${j.id}`}
            >
              <div>
                <div className="font-semibold text-sapphire">{j.property?.name || "Property"}</div>
                <div className="text-sm text-muted-foreground">{j.date} · {jobTime(j)} · {j.cleaner?.name || "Unassigned"}</div>
              </div>
              <div className="flex items-center gap-4">
                <span className="font-heading font-bold text-sapphire">{money(j.payout_amount)}</span>
                <StatusBadge status={j.status} />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
